import React from "react";
import styled from "styled-components";
import WidgetBoxes from "../../../assets/svg/article-card-skeleton.svg?react";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import profilePreferences from "@coworker/enums/profilePreferences";
import {
  CustomTestBuyProduct,
  TestBuyProduct,
} from "@coworker/types/lib/testbuy";
import { postToTestbuyService } from "../../../hooks/API/testbuy.service";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "../../../hooks/useProfilePreferencesQuery";
import { SimpleToast } from "@coworker/apprestructured/src/shared/simple/SimpleToast/SimpleToast";
import { useTranslation } from "@coworker/locales";
import ScannerErrorComponent from "@coworker/apprestructured/src/scan-and-search/components/error-component/ScannerErrorComponent";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import PlusIconPath from "@ingka/ssr-icon/paths/plus";
import { ExtractedArticleInformationType } from "@coworker/apprestructured/src/scan-and-search/types/ExtractedArticleInformationType";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import DefaultScannerComponentCameraControlled from "@coworker/apprestructured/src/scan-and-search/DefaultScannerComponentCameraControlled";

interface CustomArticleWidgetProps {
  articles: TestBuyProduct[];
  setArticles: React.Dispatch<React.SetStateAction<TestBuyProduct[]>>;
}

interface Response {
  data?: CustomTestBuyProduct;
}

const WidgetRowRapper = styled("div")`
  width: 100%;
  padding: 20px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0 1px var(--grey200);
`;

const StyledWidgetBoxes = styled(WidgetBoxes)`
  position: absolute;
  width: 280px;
  height: 100px;
  left: 20px;
  top: 0;
`;

export const CustomArticleWidget = ({
  articles,
  setArticles,
}: CustomArticleWidgetProps) => {
  const [error, setError] = React.useState<string | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const handleScanResult = (result: ExtractedArticleInformationType) => {
    const productNo = result.articleNumber;
    if (allArticleNumbers.includes(productNo)) {
      setError("ARTICLE_ALREADY_EXISTS");
      setIsSearching(false);
      return;
    }
    (async () => {
      await postToTestbuyService(
        "testbuy/custom_article",
        {
          articleId: productNo,
          storeId,
          locale: userLanguage,
        },
        (data: CustomTestBuyProduct) => (res.data = data)
      );

      if (res.data) {
        setArticles([...articles, res.data] as TestBuyProduct[]);
      } else {
        setToastVisible(true);
      }
    })();
    setIsSearching(false);
  };
  const { t } = useTranslation();
  const storeId: string = useStoreId();
  const allArticleNumbers = articles.map((article) => article?.shortId);
  const [toastVisible, setToastVisible] = React.useState(false);
  const store = useMyStore();
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    store?.primaryLocale
  );
  const res: Response = {};

  return (
    <WidgetRowRapper>
      <FixaModal
        visible={isSearching}
        handleCloseBtn={() => setIsSearching(false)}
      >
        <FixaSheets
          header={
            <FixaModalHeader closeBtnClick={() => setIsSearching(false)} />
          }
          footer={null}
          fullSize
        >
          <DefaultScannerComponentCameraControlled
            onResult={handleScanResult}
            shouldHideSPRItems={false}
          />
        </FixaSheets>
      </FixaModal>
      <ScannerErrorComponent error={error} setError={setError} />
      <StyledWidgetBoxes />
      <FixaButton
        ssrIcon={PlusIconPath}
        iconOnly
        type={"primary"}
        onClick={async () => {
          setIsSearching(true);
        }}
      />
      {toastVisible && (
        <SimpleToast
          text={t("couldNotUseThisArticleString")}
          isOpen={toastVisible}
          onCloseRequest={() => {
            setToastVisible(false);
          }}
          ariaLabel={t("couldNotUseThisArticleString")}
        />
      )}
    </WidgetRowRapper>
  );
};
